import { Datepicker, locale, MbscDatepickerChangeEvent } from "@mobiscroll/react";
import {
  Box,
  FormControl,
  Grid2,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  type SelectChangeEvent,
  Typography,
  useTheme
} from "@mui/material";
import { DateTime } from "luxon";
import type { Dispatch, SetStateAction } from "react";
import { useLayoutEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "../../../app/helpers";
import MultiColorProgressBar from "../../../components/Reports/MultiColorProgressBar/MultiColorProgressBar";
import { setInputs } from "../../Booking-Form/slices/booking.slice";
import { BookingScheduleInterface } from "../../Booking-Form/typings/booking.types";
import { LocationUsage } from "../../Reports/typings/reports.types";
import useTimeFormat from "../../../hooks/useTimeFormat/useTimeFormat";
import {
  buildResourceBookDayAvailabilitySegments,
  clampResourceBookChosenTimesToLeadTime,
  formatResourceBookSegmentClockRange,
  fullDayMinutes,
  listIsoDaysInclusive,
  resourceBookTimePickerValidRanges,
  segmentDurationMinutes
} from "../functions/buildResourceBookDayAvailabilitySegments";
import {
  scheduleResourceBookNeutralSurfaceSx,
  scheduleResourceBookPrimarySurfaceSx
} from "./scheduleResourceBookSectionSurfaces";

/** When the dialog loads a wide date span (e.g. full month), the Mobiscroll from/to pickers stay disabled; choosing a day in the occupancy dropdown must then set the real booking day in the parent. Short spans (e.g. a two-day drag) keep the dropdown as view-only across those days. */
const WIDE_BOOKING_RANGE_DAY_COUNT_THRESHOLD = 7;

const SECTION_OVERLINE_SX = {
  color: "text.secondary",
  display: "block",
  fontSize: "0.875rem",
  fontWeight: 600,
  letterSpacing: 0.1,
  lineHeight: 1.45,
  mb: 2
} as const;

function progressDataForDay(
  dayIso: string,
  schedules: BookingScheduleInterface[],
  timezone: string,
  themeSuccess: string,
  themeError: string,
  themePast: string,
  bookingLeadTimeHours: number | null | undefined
): LocationUsage[] {
  const segs = buildResourceBookDayAvailabilitySegments(dayIso, schedules, timezone, {
    bookingLeadTimeHours
  });
  const dayMinutes = fullDayMinutes();
  return segs.map(seg => {
    const minutes = segmentDurationMinutes(seg);
    let locationColor = themeError;
    if (seg.kind === "available") {
      locationColor = themeSuccess;
    } else if (seg.kind === "past") {
      locationColor = themePast;
    }
    return {
      locationName: formatResourceBookSegmentClockRange(seg.startMs, seg.endMs, dayIso, timezone),
      locationColor,
      relativeDuration: minutes / dayMinutes,
      absoluteDuration: minutes / 60,
      locationInventoryId: 0,
      isMobileWorking: false
    };
  });
}

type P = {
  bookingFrom: string;
  bookingTo: string;
  /** Hours before start (location TZ); null/0 = no lead window. */
  bookingLeadTime?: number | null;
  chosenTimes: { endTime: string; startTime: string };
  isFetching: boolean;
  loadError: boolean;
  schedules: BookingScheduleInterface[];
  setChosenTimes: Dispatch<
    SetStateAction<{
      endTime: string;
      startTime: string;
    }>
  >;
  timezone: string;
  /** Fired when the user picks a day in the occupancy dropdown and the loaded range spans more than seven days. */
  onWideRangeOccupancyDaySelected?: (dayIso: string) => void;
};

export function ScheduleResourceBookDateStep({
  bookingFrom,
  bookingTo,
  bookingLeadTime = null,
  chosenTimes,
  isFetching,
  loadError,
  schedules,
  setChosenTimes,
  timezone,
  onWideRangeOccupancyDaySelected
}: Readonly<P>) {
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const { dateFormat, timeFormat } = useTimeFormat();

  const daysInRange = useMemo(
    () => listIsoDaysInclusive(bookingFrom, bookingTo),
    [bookingFrom, bookingTo]
  );
  const rangeInvalid = daysInRange.length === 0;

  const [detailDayIso, setDetailDayIso] = useState("");

  /** Always a day inside `daysInRange` so the occupancy Select never renders an out-of-range value before effects run. */
  const detailIso = useMemo(() => {
    if (daysInRange.length === 0) return "";
    if (detailDayIso && daysInRange.includes(detailDayIso)) return detailDayIso;
    return daysInRange[0] ?? "";
  }, [daysInRange, detailDayIso]);

  useLayoutEffect(() => {
    if (daysInRange.length === 0) return;
    const next = daysInRange.includes(detailDayIso) ? detailDayIso : daysInRange[0] ?? "";
    if (next !== detailDayIso) {
      setDetailDayIso(next);
    }
  }, [bookingFrom, bookingTo, daysInRange, detailDayIso]);

  const timePickerValid = useMemo(
    () =>
      resourceBookTimePickerValidRanges({
        bookingFromIso: bookingFrom,
        bookingLeadTimeHours: bookingLeadTime,
        timezone
      }),
    [bookingFrom, bookingLeadTime, timezone]
  );

  useLayoutEffect(() => {
    if (rangeInvalid) return;
    setChosenTimes(prev => {
      const clamped = clampResourceBookChosenTimesToLeadTime({
        bookingFromIso: bookingFrom,
        bookingToIso: bookingTo,
        bookingLeadTimeHours: bookingLeadTime,
        chosenTimes: prev,
        timezone
      });
      if (clamped.startTime === prev.startTime && clamped.endTime === prev.endTime) {
        return prev;
      }
      dispatch(
        setInputs({
          bookingEnd: clamped.endTime,
          bookingFrom,
          bookingStart: clamped.startTime,
          bookingTo
        })
      );
      return clamped;
    });
  }, [bookingFrom, bookingLeadTime, bookingTo, dispatch, rangeInvalid, setChosenTimes, timezone]);

  const fromPickerValue = useMemo(() => {
    const d = DateTime.fromISO(bookingFrom).setZone(timezone);
    return d.isValid ? d.startOf("day").toJSDate() : null;
  }, [bookingFrom, timezone]);

  const toPickerValue = useMemo(() => {
    const d = DateTime.fromISO(bookingTo).setZone(timezone);
    return d.isValid ? d.startOf("day").toJSDate() : null;
  }, [bookingTo, timezone]);

  function onChangeResourceBookTimeRange(picker: MbscDatepickerChangeEvent) {
    if (!picker.value) return;
    if (!Array.isArray(picker.value)) return;

    const newStart = picker.value[0]
      ? DateTime.fromJSDate(picker.value[0] as Date).toFormat("HH:mm")
      : "08:00";
    const newEnd = picker.value[1]
      ? DateTime.fromJSDate(picker.value[1] as Date).toFormat("HH:mm")
      : "17:00";

    const clamped = clampResourceBookChosenTimesToLeadTime({
      bookingFromIso: bookingFrom,
      bookingToIso: bookingTo,
      bookingLeadTimeHours: bookingLeadTime,
      chosenTimes: { endTime: newEnd, startTime: newStart },
      timezone
    });

    setChosenTimes(prev => ({
      ...prev,
      endTime: clamped.endTime,
      startTime: clamped.startTime
    }));
    dispatch(
      setInputs({
        bookingEnd: clamped.endTime,
        bookingFrom,
        bookingStart: clamped.startTime,
        bookingTo
      })
    );
  }

  function handleDetailDayChange(ev: SelectChangeEvent<string>) {
    const dayIso = ev.target.value;
    setDetailDayIso(dayIso);
    if (daysInRange.length > WIDE_BOOKING_RANGE_DAY_COUNT_THRESHOLD) {
      onWideRangeOccupancyDaySelected?.(dayIso);
    }
  }

  return (
    <Grid2 container spacing={2} sx={{ mt: 1 }}>
      {loadError && (
        <Grid2 size={12}>
          <Typography color="error" variant="body2">
            {t("_scheduleResourceBook_scheduleLoadError")}
          </Typography>
        </Grid2>
      )}

      {rangeInvalid && (
        <Grid2 size={12}>
          <Typography color="error" variant="body2">
            {t("_scheduleResourceBook_invalidDateRange")}
          </Typography>
        </Grid2>
      )}

      {isFetching && !loadError && (
        <Grid2 size={12}>
          <Typography color="text.secondary" variant="body2">
            {t("Loading...")}
          </Typography>
        </Grid2>
      )}

      {!rangeInvalid && (
        <Grid2 size={12}>
          <Paper
            data-testid="schedule-resource-book-availability-section"
            elevation={0}
            sx={muiTheme => ({
              ...scheduleResourceBookPrimarySurfaceSx(muiTheme),
              p: { sm: 2.5, xs: 2 }
            })}
            variant="outlined"
          >
            <Typography component="h3" sx={SECTION_OVERLINE_SX} variant="overline">
              {t("_scheduleResourceBook_sectionAvailability")}
            </Typography>
            <Box
              sx={{
                alignItems: { xs: "stretch", sm: "flex-end" },
                columnGap: 2,
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                flexWrap: "wrap",
                rowGap: 2
              }}
            >
              <FormControl
                size="small"
                sx={{ flexShrink: 0, maxWidth: { sm: 280 }, width: { sm: "auto", xs: "100%" } }}
              >
                <InputLabel id="schedule-resource-book-occupancy-day-label">
                  {t("_scheduleResourceBook_occupancyShowDay")}
                </InputLabel>
                <Select
                  id="schedule-resource-book-occupancy-day"
                  label={t("_scheduleResourceBook_occupancyShowDay")}
                  labelId="schedule-resource-book-occupancy-day-label"
                  onChange={handleDetailDayChange}
                  value={detailIso}
                >
                  {daysInRange.map(dayIso => (
                    <MenuItem key={dayIso} value={dayIso}>
                      {DateTime.fromISO(dayIso).setZone(timezone).toFormat(dateFormat)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Box
                data-testid={`schedule-resource-book-day-bar-${detailIso}`}
                sx={{
                  borderRadius: 1,
                  flex: { sm: "1 1 200px" },
                  height: 56,
                  minWidth: 0,
                  overflow: "hidden",
                  pt: 2,
                  px: { sm: 0.5 },
                  width: { sm: "auto", xs: "100%" },
                  alignContent: "center"
                }}
              >
                <MultiColorProgressBar
                  data={progressDataForDay(
                    detailIso,
                    schedules,
                    timezone,
                    theme.palette.success.main,
                    theme.palette.error.main,
                    theme.palette.grey[400],
                    bookingLeadTime
                  )}
                  percent
                />
              </Box>
            </Box>
          </Paper>
        </Grid2>
      )}

      <Grid2 size={12}>
        <Paper
          data-testid="schedule-resource-book-datetime-section"
          elevation={0}
          sx={muiTheme => ({
            ...scheduleResourceBookNeutralSurfaceSx(muiTheme),
            p: { sm: 1.5, xs: 1.25 }
          })}
          variant="outlined"
        >
          <Typography component="h3" sx={{ ...SECTION_OVERLINE_SX, mb: 0 }} variant="overline">
            {t("_scheduleResourceBook_bookingRange")}
          </Typography>
          <Box
            sx={{
              alignItems: { xs: "stretch", sm: "flex-end" },
              columnGap: 1.5,
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              flexWrap: "wrap",
              rowGap: 1.25
            }}
          >
            <FormControl data-testid="datepicker-control" sx={{ flex: { sm: "0 1 auto" } }}>
              <Box
                sx={{
                  alignItems: { xs: "stretch", sm: "flex-end" },
                  columnGap: 2,
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  flexWrap: "wrap",
                  rowGap: 1
                }}
              >
                <Datepicker
                  data-testid="schedule-resource-book-date-from"
                  dateFormat={dateFormat.toUpperCase()}
                  disabled
                  label={t("_scheduleResourceBook_dateFrom")}
                  locale={locale[i18n.language]}
                  onChange={() => {}}
                  value={fromPickerValue}
                />
                <Datepicker
                  data-testid="schedule-resource-book-date-to"
                  dateFormat={dateFormat.toUpperCase()}
                  disabled
                  label={t("_scheduleResourceBook_dateTo")}
                  locale={locale[i18n.language]}
                  onChange={() => {}}
                  value={toPickerValue}
                />
              </Box>
            </FormControl>

            {!rangeInvalid && (
              <FormControl
                data-testid="schedule-resource-book-time-range-control"
                sx={{
                  flex: { sm: "1 1 220px" },
                  minWidth: { sm: 200 },
                  width: { xs: "100%", sm: "auto" }
                }}
              >
                <Datepicker
                  controls={["time"]}
                  label={t("Time")}
                  data-testid="schedule-resource-book-time-range"
                  disabled={timePickerValid.length === 0}
                  locale={locale[i18n.language]}
                  onChange={onChangeResourceBookTimeRange}
                  select="range"
                  stepMinute={15}
                  timeFormat={timeFormat}
                  value={[chosenTimes.startTime, chosenTimes.endTime]}
                  valid={
                    timePickerValid.length > 0
                      ? timePickerValid
                      : [{ start: "00:00", end: "00:00" }]
                  }
                />
              </FormControl>
            )}
          </Box>
          {!rangeInvalid && timePickerValid.length === 0 && (
            <Typography
              color="warning.main"
              data-testid="schedule-resource-book-lead-time-blocks-day"
              sx={{ mt: 1.5 }}
              variant="body2"
            >
              {t("_scheduleResourceBook_leadTimeBlocksStartDay")}
            </Typography>
          )}
        </Paper>
      </Grid2>
    </Grid2>
  );
}
